/**
 * PRD §22 — GA4 dataLayer wrapper for the consumer flow.
 *
 * We don't ship the gtag.js loader from this file; the `G-DK5KSNYEGZ`
 * container snippet lives in `index.html` only on the consumer host
 * (`localhost:5272/public-surveys/*` in dev, the public domain in prod).
 * Admins never load GA — there's nothing to measure there and it would
 * leak ID-shaped strings into a privacy-sensitive surface.
 *
 * Usage:
 *   import { gaEvent } from '@/lib/analytics/ga4';
 *   gaEvent('survey_start', { surveyId, stationId });
 *
 * If GA isn't loaded (admin pages, unit tests, SSR), the call is a
 * no-op — we never throw on missing dataLayer.
 */
export type GaEventName =
  | 'survey_start'
  | 'lda_verified'
  | 'consent_terms_accepted'
  | 'consent_marketing_accepted'
  | 'wizard_step'
  | 'game_play'
  | 'prize_won'
  | 'claim_code_shown'
  | 'claim_code_redeemed';

export interface GaEventParams {
  [key: string]: string | number | boolean | undefined;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

export function gaEvent(name: GaEventName, params: GaEventParams = {}): void {
  if (typeof window === 'undefined') return;
  if (!window.dataLayer) return;
  // gtag-style event call. The container interprets the first element as
  // the command ('event'), the second as the event name, the third as the
  // params dictionary.
  window.dataLayer.push({ event: name, ...params });
}
